import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useOrganizations } from '@/features/organizations';
import type { Character } from '@/lib/types';
import { hasValue } from '@/lib/utils/has-value';

import { FieldDisplay } from './field-display';

interface SectionCharacterMainProps {
  data: Partial<Character>;
}

/**
 * Main identity section for Character view pages.
 */
export const SectionCharacterMain = ({ data }: SectionCharacterMainProps) => {
  const { t } = useTranslation();
  const { organizations } = useOrganizations();
  const organizationNames = useMemo(() => {
    if (!data.organizationIds?.length) return [];

    const organizationNameById = new Map(organizations.map((organization) => [organization.id, organization.name]));
    return data.organizationIds.map((organizationId) => organizationNameById.get(organizationId) || organizationId);
  }, [data.organizationIds, organizations]);

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h2" sx={{ mb: 2 }}>
        Identity
      </Typography>
      <Grid container spacing={3}>
        {hasValue(data.type) && (
          <Grid size={{ xs: 12, md: 6 }}>
            <FieldDisplay label="Type" value={data.type} />
          </Grid>
        )}
        {hasValue(data.shortDescription) && (
          <Grid size={{ xs: 12 }}>
            <FieldDisplay label="Short Description" value={data.shortDescription} type="textarea" />
          </Grid>
        )}
        {hasValue(data.fullName) && (
          <Grid size={{ xs: 12, md: 6 }}>
            <FieldDisplay label="Full Name" value={data.fullName} />
          </Grid>
        )}
        {hasValue(data.nickname) && (
          <Grid size={{ xs: 12, md: 6 }}>
            <FieldDisplay label="Nickname" value={data.nickname} />
          </Grid>
        )}
        {hasValue(data.characterClass) && (
          <Grid size={{ xs: 12, md: 6 }}>
            <FieldDisplay label="Class" value={data.characterClass} />
          </Grid>
        )}
        {organizationNames.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <FieldDisplay label={t('entity_form.characters.labels.organizations')} value={organizationNames} />
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};
